import { useEffect, useState } from 'react';
import { getStores } from '../../services/analyticsservice';

interface StoreFilterProps {
  value: string;
  onChange: (value: string) => void;
}

// Module-level cache so the dropdown only fetches once per browser session.
let storesCache: string[] | null = null;
let storesPromise: Promise<string[]> | null = null;

async function fetchStores(): Promise<string[]> {
  if (storesCache) return storesCache;
  if (storesPromise) return storesPromise;
  storesPromise = (async () => {
    try {
      const data = await getStores();
      if (Array.isArray(data)) {
        storesCache = data;
        return data;
      }
      return [];
    } catch {
      return [];
    } finally {
      storesPromise = null;
    }
  })();
  return storesPromise;
}

export default function StoreFilter({ value, onChange }: StoreFilterProps) {
  const [stores, setStores] = useState<string[]>(storesCache ?? []);
  const [loading, setLoading] = useState(storesCache === null);

  useEffect(() => {
    let active = true;
    if (storesCache) {
      setStores(storesCache);
      setLoading(false);
      return;
    }
    fetchStores().then((s) => {
      if (active) {
        setStores(s);
        setLoading(false);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="flex items-center gap-2">
      <label className="text-sm font-medium text-gray-600">Store</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={loading}
        className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-40 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-60"
      >
        <option value="">All stores</option>
        {stores.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
    </div>
  );
}
